import { useCallback, useEffect, useRef, useState } from 'react';
import { BUILD_REVEAL_MS } from './builderWorkspace.constants';

export type BuildStage = 'sketch' | 'building' | 'revealing' | 'idle' | 'ready';

interface BuildStageInput {
  readonly freshBuild: boolean;
  readonly building: boolean;
  readonly hasPage: boolean;
  /** Once the building screen has faded off the finished app. */
  readonly onFirstBuildReady: () => void;
  /** The first build ended without one: stopped, failed or waiting on a question. */
  readonly onFirstBuildWithoutPage: () => void;
}

type FirstBuild = 'waiting' | 'running' | 'ended' | 'ready' | 'revealed' | 'none';

const nextPhase = (phase: FirstBuild, building: boolean, hasPage: boolean, sendOver: boolean): FirstBuild => {
  if (phase === 'waiting' && building) return 'running';
  // A send that failed before it got out, or was stopped first, never shows as building.
  if (phase === 'waiting' && sendOver) return hasPage ? 'ready' : 'ended';
  if (phase === 'running' && !building) return hasPage ? 'ready' : 'ended';
  // The draft holding the page can land a moment after the turn ends.
  if (phase === 'ended' && hasPage) return 'ready';
  return phase;
};

export const coversTheApp = (stage: BuildStage) => stage === 'sketch' || stage === 'building' || stage === 'revealing';

const firstBuildStage = (phase: FirstBuild, sketchDone: boolean): BuildStage => {
  if (phase === 'ready') return 'revealing';
  return sketchDone ? 'building' : 'sketch';
};

/**
 * The sketch plays only for a build sent from the Create App dialog, never on a reload, reconnect or later turn.
 * That build stays behind the buddies until its turn is over, even once its page exists.
 */
export const useBuildStage = ({
  freshBuild,
  building,
  hasPage,
  onFirstBuildReady,
  onFirstBuildWithoutPage,
}: BuildStageInput) => {
  const [phase, setPhase] = useState<FirstBuild>(freshBuild ? 'waiting' : 'none');
  const [sketchDone, setSketchDone] = useState(false);
  const [sendOver, setSendOver] = useState(false);
  const told = useRef<FirstBuild>(phase);
  const endSketch = useCallback(() => setSketchDone(true), []);
  const endFirstSend = useCallback(() => setSendOver(true), []);

  const next = nextPhase(phase, building, hasPage, sendOver);
  if (next !== phase) setPhase(next);

  let stage: BuildStage = 'idle';
  if (phase === 'waiting' || phase === 'running' || phase === 'ready') stage = firstBuildStage(phase, sketchDone);
  else if (hasPage) stage = 'ready';
  else if (building) stage = 'building';

  useEffect(() => {
    if (stage !== 'revealing') return undefined;
    const id = window.setTimeout(() => setPhase('revealed'), BUILD_REVEAL_MS);
    return () => window.clearTimeout(id);
  }, [stage]);

  useEffect(() => {
    if (told.current === phase) return;
    told.current = phase;
    if (phase === 'ended') onFirstBuildWithoutPage();
    if (phase === 'revealed') onFirstBuildReady();
  }, [onFirstBuildReady, onFirstBuildWithoutPage, phase]);

  return { stage, endSketch, endFirstSend };
};
